import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAgency } from '@/contexts/AgencyContext';
import { logUnauthorizedAccess } from '@/lib/audit';
import { resolveLandingRoute } from '@/lib/auth-landing';
import { Loader2 } from 'lucide-react';




/**
 * Allowed role tokens — DELIBERATELY narrow + non-overlapping. Use the right
 * token for the route's intent; never reach for "super_admin" to mean "owner".
 *
 *  - 'super_admin'   → Platform staff only (rows in `super_admins` table).
 *                       Resolves via `isSuperAdmin`. Use for /superadmin/*.
 *  - 'agency_admin'  → Agency owner OR admin within their own agency workspace.
 *                       Resolves via `isAgencyAdmin` (agency_role check, NOT
 *                       inherited from platform staff). Use for /employees,
 *                       /dashboard, billing settings, etc.
 *  - 'agency_staff'  → Any agency-side user (owner/admin/manager/staff).
 *                       Resolves via the `user_roles` row.
 *  - 'brand_client'  → Brand portal users. Resolves via the `user_roles` row.
 */
interface Props {
  children: React.ReactNode;
  allowedRoles?: ('super_admin' | 'agency_admin' | 'agency_staff' | 'brand_client' | 'creator')[];
}

const ONBOARDING_EXEMPT_PREFIXES = ['/onboarding', '/portal', '/brand/', '/settings', '/invite', '/verify-email', '/creator'];
// Approval gate removed — invited users get instant access. The
// `/pending-approval` route still exists as a soft fallback for users
// without an agency_id, but no path actively redirects to it.

export default function ProtectedRoute({ children, allowedRoles }: Props) {
  const { user, role, loading: authLoading } = useAuth();
  const { onboardingComplete, agencyId, agencyRole, isSuperAdmin, isAgencyAdmin, approvalStatus, loading: agencyLoading } = useAgency();
  const location = useLocation();

  if (authLoading || agencyLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (!user.email_confirmed_at && !location.pathname.startsWith('/verify-email')) {
    return <Navigate to="/verify-email" replace />;
  }

  // Role-aware default landing — delegated to the shared resolver so Login
  // and ProtectedRoute always agree (no redirect bounce between /me, /dashboard,
  // /portal, /superadmin, /onboarding, /pending-approval).
  const defaultRoute = resolveLandingRoute({
    user,
    role: role as any,
    isSuperAdmin,
    agencyId,
    agencyRole,
    onboardingComplete,
    approvalStatus,
  });


  // HARD ISOLATION: platform_super_admin must NEVER mount agency workspace
  // routes/layouts. The only places they may land are /superadmin/* (and a
  // narrow set of neutral pages). Everything else redirects to /superadmin.
  // A future audited "View as Agency" flow can opt out via a session flag.
  if (isSuperAdmin) {
    const platformAllowed =
      location.pathname.startsWith('/superadmin') ||
      location.pathname.startsWith('/verify-email') ||
      location.pathname.startsWith('/pending-approval');
    if (!platformAllowed) {
      return <Navigate to="/superadmin" replace />;
    }
  }

  // Permission resolution — allowedRoles is an OR-list of grants. A user is
  // allowed if they satisfy ANY token in the list. Tokens:
  //   - 'super_admin'  → platform staff (isSuperAdmin)
  //   - 'agency_admin' → agency owner/admin within their agency (isAgencyAdmin)
  //   - 'agency_staff' → any authenticated agency-side user (role !== 'brand_client')
  //   - 'brand_client' → brand portal users (role === 'brand_client')
  if (allowedRoles && allowedRoles.length > 0) {
    const isAgencyUserRole = !!user && role !== 'brand_client' && role !== 'creator' && !isSuperAdmin;
    const grants: Record<NonNullable<Props['allowedRoles']>[number], boolean> = {
      super_admin: isSuperAdmin,
      agency_admin: isAgencyAdmin,
      agency_staff: isAgencyUserRole || isAgencyAdmin,
      brand_client: role === 'brand_client',
      creator: role === 'creator',
    };
    const allowed = allowedRoles.some((t) => grants[t]);
    if (!allowed) {
      const reason = allowedRoles.length === 1 && allowedRoles[0] === 'super_admin'
        ? 'not_super_admin'
        : allowedRoles.length === 1 && allowedRoles[0] === 'agency_admin'
          ? (isSuperAdmin ? 'platform_staff_blocked_from_agency_admin' : 'not_agency_admin')
          : 'role_not_permitted';
      logUnauthorizedAccess(location.pathname, reason);
      // Avoid redirect loops: if defaultRoute equals current path, send to /login fallback.
      const target = defaultRoute === location.pathname ? '/login' : defaultRoute;
      return <Navigate to={target} replace state={{ accessDenied: true, attempted: location.pathname }} />;
    }
  }


  // No approval gate — owners self-serve their workspace. Just enforce the
  // onboarding redirect for agency users mid-setup.
  const isAgencyUser = !isSuperAdmin && role !== 'brand_client' && role !== 'creator';
  const isExempt = ONBOARDING_EXEMPT_PREFIXES.some((p) => location.pathname.startsWith(p));
  if (isAgencyUser && agencyId && !onboardingComplete && !isExempt) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
